/**
 * Duplicate Overview Component
 * Displays duplicate statistics and allows navigation to duplicate management
 */

import React from 'react';
import { Copy, ExternalLink, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Format currency value
 */
const formatCurrency = (value) => {
  if (value == null) return '$0';
  const num = parseFloat(value);
  if (num >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
};

/**
 * Format number with commas
 */
const formatNumber = (value) => {
  if (value == null) return '0';
  return parseInt(value, 10).toLocaleString();
};

/**
 * Duplicate Overview Component
 */
const DuplicateOverview = ({ duplicates }) => {
  const navigate = useNavigate();

  if (!duplicates || duplicates.length === 0) {
    return null;
  }

  // Get total row (first row with department_id = 0 or null)
  const totalRow = duplicates.find(d => !d.department_id || d.department_id === 0);

  // Get top departments by duplicate count
  const topDuplicates = duplicates
    .filter(d => d.department_id && d.department_id !== 0)
    .sort((a, b) => (b.duplicate_count || 0) - (a.duplicate_count || 0))
    .slice(0, 5);

  if (!totalRow && topDuplicates.length === 0) {
    return null;
  }

  return (
    <div className="dga-duplicate-overview">
      <div className="dga-duplicate-header">
        <div className="dga-duplicate-title">
          <Copy size={18} />
          <h3>Duplicate Records</h3>
        </div>
        <button
          className="dga-btn dga-btn-ghost dga-btn-sm"
          onClick={() => navigate('/duplicates')}
          title="Manage duplicates"
        >
          <ExternalLink size={14} />
        </button>
      </div>

      {totalRow && (
        <div className="dga-duplicate-total">
          <div className="dga-duplicate-stat">
            <span className="dga-duplicate-value">
              {formatNumber(totalRow.duplicate_count)}
            </span>
            <span className="dga-duplicate-label">Total Duplicates</span>
          </div>
          <div className="dga-duplicate-stat">
            <span className="dga-duplicate-value dga-text-warning">
              {parseFloat(totalRow.duplicate_percentage || 0).toFixed(1)}%
            </span>
            <span className="dga-duplicate-label">of Records</span>
          </div>
          <div className="dga-duplicate-stat">
            <span className="dga-duplicate-value dga-text-error">
              {formatCurrency(totalRow.duplicate_spend)}
            </span>
            <span className="dga-duplicate-label">Duplicate Spend</span>
          </div>
        </div>
      )}

      {topDuplicates.length > 0 && (
        <>
          <div className="dga-duplicate-divider" />
          <div className="dga-duplicate-list">
            <h4>Top Departments</h4>
            {topDuplicates.map(dept => (
              <div key={dept.department_id} className="dga-duplicate-item">
                <div className="dga-duplicate-dept">
                  <span className="dga-duplicate-acronym">{dept.acronym || '—'}</span>
                  <span className="dga-duplicate-name">{dept.department_name}</span>
                </div>
                <div className="dga-duplicate-count">
                  <span className="dga-duplicate-num">{formatNumber(dept.duplicate_count)}</span>
                  <span className="dga-duplicate-pct">({parseFloat(dept.duplicate_percentage || 0).toFixed(1)}%)</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {totalRow && parseFloat(totalRow.duplicate_percentage) > 5 && (
        <div className="dga-duplicate-warning">
          <AlertTriangle size={14} />
          <span>High duplicate rate detected. Consider running deduplication.</span>
        </div>
      )}

      <style>{`
        .dga-duplicate-overview {
          background: var(--color-bg-secondary, #1a1a1a);
          border: 1px solid var(--color-border, #333);
          border-radius: 12px;
          padding: 16px;
          margin-top: 24px;
        }

        .dga-duplicate-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .dga-duplicate-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .dga-duplicate-title svg {
          color: var(--color-warning, #f59e0b);
        }

        .dga-duplicate-title h3 {
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--color-text-primary, #fff);
          margin: 0;
        }

        .dga-duplicate-total {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .dga-duplicate-stat {
          text-align: center;
          padding: 12px 8px;
          background: var(--color-bg-tertiary, #252525);
          border-radius: 8px;
        }

        .dga-duplicate-value {
          display: block;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--color-text-primary, #fff);
        }

        .dga-duplicate-value.dga-text-warning {
          color: var(--color-warning, #f59e0b);
        }

        .dga-duplicate-value.dga-text-error {
          color: var(--color-error, #ef4444);
        }

        .dga-duplicate-label {
          font-size: 0.6875rem;
          color: var(--color-text-tertiary, #666);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .dga-duplicate-divider {
          height: 1px;
          background: var(--color-border, #333);
          margin: 16px 0;
        }

        .dga-duplicate-list h4 {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-secondary, #888);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin: 0 0 12px;
        }

        .dga-duplicate-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid var(--color-border, #333);
        }

        .dga-duplicate-item:last-child {
          border-bottom: none;
        }

        .dga-duplicate-dept {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }

        .dga-duplicate-acronym {
          font-size: 0.625rem;
          font-weight: 600;
          color: var(--color-primary, #6366f1);
          background: rgba(99, 102, 241, 0.1);
          padding: 2px 6px;
          border-radius: 4px;
          flex-shrink: 0;
        }

        .dga-duplicate-name {
          font-size: 0.8125rem;
          color: var(--color-text-primary, #fff);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .dga-duplicate-count {
          display: flex;
          align-items: baseline;
          gap: 4px;
          flex-shrink: 0;
        }

        .dga-duplicate-num {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--color-warning, #f59e0b);
        }

        .dga-duplicate-pct {
          font-size: 0.75rem;
          color: var(--color-text-tertiary, #666);
        }

        .dga-duplicate-warning {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
          padding: 10px 12px;
          background: rgba(245, 158, 11, 0.1);
          border-radius: 6px;
          font-size: 0.8125rem;
          color: var(--color-warning, #f59e0b);
        }

        .dga-duplicate-warning svg {
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
};

export default DuplicateOverview;
